/**
 * AdminDashboard - SaaS analytics, cost tracking, and system monitoring.
 * Matches Settings page aesthetic with dark mode support.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Database, Users, MessageSquare, Activity, RefreshCw, Bell,
  CheckCircle, XCircle, AlertTriangle, Clock, Send, ArrowLeft,
  DollarSign, Zap, TrendingUp, BarChart3, Shield, Cpu,
  Landmark, CreditCard, Link2, FileText, UserCheck, Layers,
  Settings, RotateCcw, ChevronDown, ChevronUp
} from 'lucide-react'

/** Formats a number as USD currency */
const fmt = (n, decimals = 2) => `$${Number(n || 0).toFixed(decimals)}`

/** Formats large numbers with commas */
const fmtNum = (n) => Number(n || 0).toLocaleString()

/** Compact token display (e.g. 1.2M, 450K) */
const fmtTokens = (n) => {
  if (!n) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

/** Format percentage */
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`

/** Truncate string to max length with ellipsis */
const truncate = (s, max = 60) => {
  if (!s) return ''
  return s.length > max ? s.slice(0, max) + '...' : s
}

/** Friendly labels for rephrase event types */
const REPHRASE_TYPE_LABELS = {
  explicit_retry: 'Explicit Retry',
  word_overlap: 'Rephrased Query',
  quick_followup: 'Quick Follow-up',
  post_thumbsdown: 'After Thumbs Down',
}

const PERIODS = [
  { key: 'current_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_90_days', label: '90 Days' },
  { key: 'all_time', label: 'All Time' },
]

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState('current_month')
  const [analytics, setAnalytics] = useState(null)
  const [status, setStatus] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [testingAlert, setTestingAlert] = useState(false)
  const [alertResult, setAlertResult] = useState(null)
  const [rephraseStats, setRephraseStats] = useState(null)
  const [rephraseEventsOpen, setRephraseEventsOpen] = useState(false)

  const fetchData = useCallback(async (p) => {
    setLoading(true)
    setError(null)
    const activePeriod = p || period

    try {
      const token = localStorage.getItem('amtrend_token')
      const headers = { Authorization: `Bearer ${token}` }

      const [statusRes, statsRes, analyticsRes, rephraseRes] = await Promise.all([
        fetch('/api/admin/status', { headers }),
        fetch('/api/admin/stats', { headers }),
        fetch(`/api/admin/analytics?period=${activePeriod}`, { headers }).catch(() => null),
        fetch(`/api/admin/rephrase-stats?period=${activePeriod}`, { headers }).catch(() => null),
      ])

      if (statusRes.status === 403) {
        setError('Admin access required')
        return
      }

      if (!statusRes.ok || !statsRes.ok) throw new Error('Failed to fetch admin data')

      setStatus(await statusRes.json())
      setStats(await statsRes.json())
      setAnalytics(analyticsRes?.ok ? await analyticsRes.json() : null)
      setRephraseStats(rephraseRes?.ok ? await rephraseRes.json() : null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchData()
    const interval = setInterval(() => fetchData(), 30000)
    return () => clearInterval(interval)
  }, [])

  // Re-fetch when period changes
  const handlePeriodChange = (p) => {
    setPeriod(p)
    fetchData(p)
  }

  const testAlert = async () => {
    setTestingAlert(true)
    setAlertResult(null)
    try {
      const token = localStorage.getItem('amtrend_token')
      const response = await fetch('/api/admin/test-alert', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      setAlertResult(await response.json())
    } catch (err) {
      setAlertResult({ error: err.message })
    } finally {
      setTestingAlert(false)
    }
  }

  if (loading && !status) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-surface-dark flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-surface-dark flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const a = analytics  // shorthand
  const isHealthy = status?.status === 'healthy'
  const dbConnected = status?.database?.connected

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-surface-dark">
      {/* Header */}
      <header className="bg-white dark:bg-surface-dark-raised border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Back to Trendy"
              >
                <ArrowLeft size={20} className="text-gray-700 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Shield size={20} className="text-purple-500" />
                  Admin Dashboard
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {a?.period?.label || 'SaaS analytics & cost tracking'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Period Selector */}
              <div className="hidden sm:flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
                {PERIODS.map(p => (
                  <button
                    key={p.key}
                    onClick={() => handlePeriodChange(p.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      period === p.key
                        ? 'bg-white dark:bg-surface-dark-raised text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => fetchData()}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                title="Settings"
              >
                <Settings size={14} />
                <span className="hidden sm:inline">Settings</span>
              </button>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amtrend-blue bg-amtrend-blue/10 rounded-lg hover:bg-amtrend-blue/20 transition-colors"
              >
                <MessageSquare size={14} />
                <span className="hidden sm:inline">Back to Trendy</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* ============================================================= */}
        {/* Section 1: Cost Overview (top-line KPIs)                       */}
        {/* ============================================================= */}
        {a && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <DollarSign size={16} className="text-emerald-600 dark:text-emerald-400" />
              Cost Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Cost */}
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 dark:bg-emerald-900/40 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Cost</p>
                    <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(a.total_cost)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                  AI: {fmt(a.ai_costs?.total_cost)} &middot; Plaid: {fmt(a.plaid_costs?.total_cost)}
                </p>
              </div>

              {/* Cost Per User */}
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-lg">
                    <UserCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cost / User</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(a.unit_economics?.cost_per_user)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                  {a.unit_economics?.active_users} active user{a.unit_economics?.active_users !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Cost Per Conversation */}
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 dark:bg-purple-900/40 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Cost / Conversation</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(a.unit_economics?.cost_per_conversation, 4)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                  {fmtNum(a.ai_costs?.conversations)} conversations
                </p>
              </div>

              {/* Projected Monthly or Cache */}
              {a.ai_costs?.projection ? (
                <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 dark:bg-amber-900/40 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Projected Monthly</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(a.ai_costs.projection.monthly_estimate)}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                    {fmt(a.ai_costs.projection.daily_average)}/day avg ({a.ai_costs.projection.days_elapsed}d elapsed)
                  </p>
                </div>
              ) : (
                <div className={`bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 ${
                  a.ai_costs?.cache?.health === 'excellent' ? 'border-green-200 dark:border-green-800' :
                  a.ai_costs?.cache?.health === 'good' ? 'border-blue-200 dark:border-blue-800' : 'border-amber-200 dark:border-amber-800'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      a.ai_costs?.cache?.health === 'excellent' ? 'bg-green-50 dark:bg-green-900/40' :
                      a.ai_costs?.cache?.health === 'good' ? 'bg-blue-50 dark:bg-blue-900/40' : 'bg-amber-50 dark:bg-amber-900/40'
                    }`}>
                      <Zap className={`w-5 h-5 ${
                        a.ai_costs?.cache?.health === 'excellent' ? 'text-green-600 dark:text-green-400' :
                        a.ai_costs?.cache?.health === 'good' ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Cache Hit Rate</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{a.ai_costs?.cache?.hit_rate_pct}%</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Saved {fmt(a.ai_costs?.cache?.savings_usd)}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 2: AI Cost Breakdown                                   */}
        {/* ============================================================= */}
        {a?.ai_costs && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Cpu size={16} className="text-blue-600 dark:text-blue-400" />
              AI Cost Breakdown
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Token Breakdown */}
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <Cpu size={15} className="text-gray-400 dark:text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Token Usage</h3>
                </div>
                <div className="p-5">
                  <div className="space-y-4">
                    <TokenBar label="Input" tokens={a.ai_costs.tokens.input} total={a.ai_costs.tokens.total} color="bg-blue-500" />
                    <TokenBar label="Output" tokens={a.ai_costs.tokens.output} total={a.ai_costs.tokens.total} color="bg-purple-500" />
                    <TokenBar label="Cache Read" tokens={a.ai_costs.tokens.cache_read} total={a.ai_costs.tokens.total} color="bg-green-500" />
                    <TokenBar label="Cache Write" tokens={a.ai_costs.tokens.cache_write} total={a.ai_costs.tokens.total} color="bg-amber-500" />
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Total: {fmtTokens(a.ai_costs.tokens.total)} tokens</span>
                    <span>{fmtNum(a.ai_costs.api_calls)} API calls</span>
                  </div>
                </div>
              </div>

              {/* Per-User Breakdown */}
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <Users size={15} className="text-gray-400 dark:text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Usage by User</h3>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-gray-700/50 max-h-80 overflow-y-auto">
                  {(a.engagement?.by_user || []).length === 0 ? (
                    <div className="px-5 py-8 text-center text-gray-400 dark:text-gray-500 text-xs">No usage data for this period.</div>
                  ) : (a.engagement.by_user).map((u, i) => (
                    <div key={i} className="px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{u.username}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">{u.role} &middot; {u.conversations} conv</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{fmt(u.cost)}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">{fmtTokens(u.total_tokens)} tok</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Per-Role Breakdown */}
            {(a.engagement?.by_role || []).length > 0 && (
              <div className="mt-6 bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <Shield size={15} className="text-gray-400 dark:text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Usage by Role</h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {a.engagement.by_role.map((r, i) => (
                      <div key={i} className="text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">{r.role}</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(r.cost)}</p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-500">{r.users} user{r.users !== 1 ? 's' : ''} &middot; {r.conversations} conv</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 3: Plaid Cost Breakdown                                */}
        {/* ============================================================= */}
        {a?.plaid_costs && a.plaid_costs.total_calls > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Landmark size={16} className="text-indigo-600 dark:text-indigo-400" />
              Plaid Banking API
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 border-indigo-200 dark:border-indigo-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 rounded-lg">
                    <DollarSign className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Plaid Cost</p>
                    <p className="text-xl font-bold text-indigo-700 dark:text-indigo-400">{fmt(a.plaid_costs.total_cost)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">{fmtPct(a.unit_economics?.plaid_cost_pct)} of total cost</p>
              </div>
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-lg">
                    <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">API Calls</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmtNum(a.plaid_costs.total_calls)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 dark:bg-green-900/40 rounded-lg">
                    <Link2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Institutions</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{a.plaid_costs.institutions?.length || 0}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">{a.plaid_costs.institutions?.join(', ') || 'None'}</p>
              </div>
            </div>

            {/* By-endpoint table */}
            {(a.plaid_costs.by_endpoint || []).length > 0 && (
              <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <FileText size={15} className="text-gray-400 dark:text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Calls by Endpoint</h3>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {a.plaid_costs.by_endpoint.map((ep, i) => (
                    <div key={i} className="px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-center justify-between transition-colors">
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-xs font-mono">{ep.endpoint}</p>
                      <div className="flex items-center gap-6">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{fmtNum(ep.calls)} calls</span>
                        {ep.cost > 0 && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">{fmt(ep.cost)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-3 text-[10px] text-gray-400 dark:text-gray-500">
              Balance check: $0.10/call &middot; Enrich: $0.01/txn &middot; Statement download: $1.50/stmt &middot; Other endpoints included in per-item monthly fee
            </p>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 4: Engagement & Adoption                               */}
        {/* ============================================================= */}
        {a?.engagement && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <UserCheck size={16} className="text-teal-600 dark:text-teal-400" />
              Engagement & Unit Economics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <MetricCard label="DAU" value={a.engagement.dau} icon={<Users size={18} className="text-blue-600 dark:text-blue-400" />} />
              <MetricCard label="MAU" value={a.engagement.mau} icon={<Users size={18} className="text-indigo-600 dark:text-indigo-400" />} />
              <MetricCard label="DAU:MAU" value={fmtPct(a.engagement.dau_mau_ratio * 100)} icon={<Activity size={18} className="text-purple-600 dark:text-purple-400" />} raw />
              <MetricCard label="Conv / User" value={a.unit_economics?.conversations_per_user} icon={<MessageSquare size={18} className="text-green-600 dark:text-green-400" />} />
              <MetricCard label="Tools / Conv" value={a.unit_economics?.tool_calls_per_conversation} icon={<Layers size={18} className="text-amber-600 dark:text-amber-400" />} />
              <MetricCard label="Cost / Tool" value={fmt(a.unit_economics?.cost_per_tool_call, 4)} icon={<DollarSign size={18} className="text-red-600 dark:text-red-400" />} raw />
            </div>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 5: Trends                                              */}
        {/* ============================================================= */}
        {a?.daily_trend?.length > 1 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-emerald-600 dark:text-emerald-400" />
              Daily Cost Trend
            </h2>
            <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <StackedBarChart data={a.daily_trend} />
            </div>
          </section>
        )}

        {a?.monthly_trend?.length > 1 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
              Monthly Cost Trend
            </h2>
            <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <MonthlyBarChart data={a.monthly_trend} />
            </div>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 6: System Status                                       */}
        {/* ============================================================= */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">System Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 ${isHealthy ? 'border-green-200 dark:border-green-800' : 'border-amber-200 dark:border-amber-800'}`}>
              <div className="flex items-center gap-3">
                {isHealthy ? <CheckCircle className="w-6 h-6 text-green-500" /> : <AlertTriangle className="w-6 h-6 text-amber-500" />}
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                  <p className={`text-lg font-bold ${isHealthy ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {isHealthy ? 'Healthy' : 'Degraded'}
                  </p>
                </div>
              </div>
            </div>
            <div className={`bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 ${dbConnected ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}`}>
              <div className="flex items-center gap-3">
                <Database className={`w-6 h-6 ${dbConnected ? 'text-green-500' : 'text-red-500'}`} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Database</p>
                  <p className={`text-lg font-bold ${dbConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {dbConnected ? 'Connected' : 'Disconnected'}
                  </p>
                </div>
              </div>
              {status?.database?.latency_ms && (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">Latency: {status.database.latency_ms.toFixed(1)}ms</p>
              )}
            </div>
            <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Uptime</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{status?.uptime_formatted || 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <Activity className="w-6 h-6 text-purple-500 dark:text-purple-400" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tools Loaded</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{status?.tools_loaded || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================= */}
        {/* Section 7: Usage Statistics                                     */}
        {/* ============================================================= */}
        {stats && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Usage Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={<Users className="w-6 h-6 text-blue-500 dark:text-blue-400" />} label="Total Users" value={stats.users.total} />
              <StatCard icon={<MessageSquare className="w-6 h-6 text-green-500 dark:text-green-400" />} label="Sessions Today" value={stats.sessions.today} />
              <StatCard icon={<MessageSquare className="w-6 h-6 text-indigo-500 dark:text-indigo-400" />} label="Messages Today" value={stats.messages.today} />
              <StatCard icon={<Activity className="w-6 h-6 text-amber-500 dark:text-amber-400" />} label="Messages This Week" value={stats.messages.this_week} />
            </div>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section 8: Users & Alerts                                      */}
        {/* ============================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stats?.users?.list && (
            <section className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Users</h2>
              </div>
              <div className="divide-y divide-gray-50 dark:divide-gray-700/50 max-h-96 overflow-y-auto">
                {stats.users.list.map((user) => (
                  <div key={user.id} className="px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{user.username}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">{user.email || 'No email'}</p>
                      </div>
                      <div className="text-right text-[10px] text-gray-500 dark:text-gray-400">
                        <p>Last login</p>
                        <p>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Alert Configuration</h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Alerts Enabled</span>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  status?.alerts_enabled
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {status?.alerts_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Send className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Alert Recipient</span>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400">{status?.admin_email}</span>
              </div>
              <hr className="border-gray-100 dark:border-gray-700" />
              <button
                onClick={testAlert}
                disabled={testingAlert}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
              >
                {testingAlert ? (
                  <><RefreshCw size={14} className="animate-spin" /> Sending...</>
                ) : (
                  <><Bell size={14} /> Send Test Alert</>
                )}
              </button>
              {alertResult && (
                <div className={`p-3 rounded-lg text-xs ${
                  alertResult.error
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                }`}>
                  {alertResult.error ? (
                    <p>Error: {alertResult.error}</p>
                  ) : (
                    <div>
                      <p className="font-medium">{alertResult.message}</p>
                      <p>Email: {alertResult.email_configured ? 'Configured via Microsoft Graph' : 'Not configured'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Recent Errors */}
        {status?.recent_errors?.length > 0 && (
          <section className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Errors</h2>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-700/50 max-h-64 overflow-y-auto">
              {status.recent_errors.map((err, index) => (
                <div key={index} className="px-5 py-3 text-xs">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-mono text-red-600 dark:text-red-400">{err.error}</p>
                      {err.context && <p className="text-gray-500 dark:text-gray-400 mt-1">Context: {err.context}</p>}
                    </div>
                    <p className="text-gray-400 dark:text-gray-500 whitespace-nowrap">{new Date(err.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ============================================================= */}
        {/* Section: Rephrase & Retry Intelligence                         */}
        {/* ============================================================= */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4 flex items-center gap-2">
            <RotateCcw size={16} className="text-orange-600 dark:text-orange-400" />
            Rephrase & Retry Intelligence
          </h2>

          {!rephraseStats ? (
            <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">No rephrase data yet</p>
            </div>
          ) : (
            <>
              {/* Top KPI cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Rephrase Rate */}
                <div className={`bg-white dark:bg-surface-dark-raised rounded-xl p-5 border-2 ${
                  rephraseStats.rephrase_rate < 5
                    ? 'border-green-200 dark:border-green-800'
                    : rephraseStats.rephrase_rate <= 10
                      ? 'border-amber-200 dark:border-amber-800'
                      : 'border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      rephraseStats.rephrase_rate < 5
                        ? 'bg-green-50 dark:bg-green-900/40'
                        : rephraseStats.rephrase_rate <= 10
                          ? 'bg-amber-50 dark:bg-amber-900/40'
                          : 'bg-red-50 dark:bg-red-900/40'
                    }`}>
                      <RotateCcw className={`w-5 h-5 ${
                        rephraseStats.rephrase_rate < 5
                          ? 'text-green-600 dark:text-green-400'
                          : rephraseStats.rephrase_rate <= 10
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-red-600 dark:text-red-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Rephrase Rate</p>
                      <p className={`text-xl font-bold ${
                        rephraseStats.rephrase_rate < 5
                          ? 'text-green-700 dark:text-green-400'
                          : rephraseStats.rephrase_rate <= 10
                            ? 'text-amber-700 dark:text-amber-400'
                            : 'text-red-700 dark:text-red-400'
                      }`}>{fmtPct(rephraseStats.rephrase_rate)}</p>
                    </div>
                  </div>
                </div>

                {/* Total Rephrases */}
                <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-50 dark:bg-orange-900/40 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Rephrases</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmtNum(rephraseStats.total_rephrases)}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                    of {fmtNum(rephraseStats.total_messages)} messages
                  </p>
                </div>

                {/* By Type breakdown */}
                <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">By Type</p>
                  </div>
                  <div className="space-y-2">
                    {(rephraseStats.by_type || []).length === 0 ? (
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">No data</p>
                    ) : (
                      rephraseStats.by_type.map((t, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-xs text-gray-600 dark:text-gray-300">{REPHRASE_TYPE_LABELS[t.type] || t.type}</span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{fmtNum(t.count)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* By User breakdown */}
                <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">Top Users</p>
                  </div>
                  <div className="space-y-2">
                    {(rephraseStats.by_user || []).length === 0 ? (
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">No data</p>
                    ) : (
                      rephraseStats.by_user.slice(0, 5).map((u, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-xs text-gray-600 dark:text-gray-300">{u.username}</span>
                          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{fmtNum(u.count)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Rephrase Events (collapsible) */}
              {(rephraseStats.recent_events || []).length > 0 && (
                <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    onClick={() => setRephraseEventsOpen(prev => !prev)}
                    className="w-full px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <RotateCcw size={15} className="text-gray-400 dark:text-gray-500" />
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Rephrase Events</h3>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">({rephraseStats.recent_events.length})</span>
                    </div>
                    {rephraseEventsOpen
                      ? <ChevronUp size={16} className="text-gray-400 dark:text-gray-500" />
                      : <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
                    }
                  </button>
                  {rephraseEventsOpen && (
                    <div className="divide-y divide-gray-50 dark:divide-gray-700/50 max-h-96 overflow-y-auto">
                      {rephraseStats.recent_events.slice(0, 10).map((evt, i) => (
                        <div key={i} className="px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{evt.username}</span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                                  {REPHRASE_TYPE_LABELS[evt.type] || evt.type}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={evt.original}>
                                <span className="text-gray-400 dark:text-gray-500">Original:</span> {truncate(evt.original, 60)}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-300 truncate" title={evt.rephrase}>
                                <span className="text-gray-400 dark:text-gray-500">Rephrase:</span> {truncate(evt.rephrase, 60)}
                              </p>
                            </div>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                              {new Date(evt.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>

      </main>
    </div>
  )
}


// =============================================================================
// Sub-components
// =============================================================================

/** Horizontal token bar with label and percentage */
function TokenBar({ label, tokens, total, color }) {
  const pct = total > 0 ? Math.round((tokens / total) * 100) : 0
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600 dark:text-gray-300">{label}</span>
        <span className="text-gray-500 dark:text-gray-400 font-mono">{fmtTokens(tokens)} ({pct}%)</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  )
}

/** Compact metric card for engagement section */
function MetricCard({ label, value, icon, raw }) {
  return (
    <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-4 border border-gray-200 dark:border-gray-700 text-center">
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{raw ? value : fmtNum(value)}</p>
      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{label}</p>
    </div>
  )
}

/** Simple stat card */
function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white dark:bg-surface-dark-raised rounded-xl p-5 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmtNum(value)}</p>
        </div>
      </div>
    </div>
  )
}

/** Stacked bar chart for daily cost trend (AI + Plaid) */
function StackedBarChart({ data }) {
  if (!data || data.length === 0) return null
  const maxCost = Math.max(...data.map(d => d.total_cost || d.ai_cost), 0.01)

  return (
    <div className="flex items-end gap-1 h-36">
      {data.map((d, i) => {
        const aiPct = Math.max((d.ai_cost / maxCost) * 100, 1)
        const plaidPct = (d.plaid_cost / maxCost) * 100
        const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative">
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                <p className="font-semibold">{dayLabel}</p>
                <p>AI: {fmt(d.ai_cost)}</p>
                {d.plaid_cost > 0 && <p>Plaid: {fmt(d.plaid_cost)}</p>}
                <p>Total: {fmt(d.total_cost)}</p>
                <p>{d.conversations} conv &middot; {d.active_users} users</p>
              </div>
            </div>
            <div className="w-full flex flex-col-reverse">
              <div
                className="w-full bg-emerald-400 hover:bg-emerald-500 rounded-t-sm transition-colors cursor-pointer"
                style={{ height: `${(aiPct / 100) * 144}px`, minHeight: '2px' }}
              />
              {plaidPct > 0 && (
                <div
                  className="w-full bg-indigo-400 hover:bg-indigo-500 transition-colors cursor-pointer"
                  style={{ height: `${(plaidPct / 100) * 144}px` }}
                />
              )}
            </div>
            {(i % 5 === 0 || i === data.length - 1) && (
              <span className="text-[9px] text-gray-400 dark:text-gray-500 mt-1 leading-none">{dayLabel}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Monthly trend bar chart */
function MonthlyBarChart({ data }) {
  if (!data || data.length === 0) return null
  const maxCost = Math.max(...data.map(d => d.total_cost), 0.01)

  return (
    <div className="flex items-end gap-2 h-36">
      {data.map((d, i) => {
        const aiPct = Math.max((d.ai_cost / maxCost) * 100, 1)
        const plaidPct = (d.plaid_cost / maxCost) * 100
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative">
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                <p className="font-semibold">{d.month}</p>
                <p>AI: {fmt(d.ai_cost)}</p>
                {d.plaid_cost > 0 && <p>Plaid: {fmt(d.plaid_cost)}</p>}
                <p>Total: {fmt(d.total_cost)}</p>
                <p>{d.conversations} conv &middot; {d.active_users} users</p>
              </div>
            </div>
            <div className="w-full flex flex-col-reverse">
              <div
                className="w-full bg-emerald-400 hover:bg-emerald-500 rounded-t transition-colors cursor-pointer"
                style={{ height: `${(aiPct / 100) * 144}px`, minHeight: '2px' }}
              />
              {plaidPct > 0 && (
                <div
                  className="w-full bg-indigo-400 hover:bg-indigo-500 transition-colors cursor-pointer"
                  style={{ height: `${(plaidPct / 100) * 144}px` }}
                />
              )}
            </div>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-none">{d.month}</span>
          </div>
        )
      })}
    </div>
  )
}
